import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/supabaseClient";

export interface DatabaseSubscription {
  id: string;
  user_id: string;
  name: string;
  amount: number;
  currency: string;
  billing_cycle: string | null;
  // New: billing_type stored in DB (e.g. 'recurring' | 'pay_as_you_go')
  billing_type?: string | null;
  category_id?: string;
  next_payment_date?: string | null;
  status: string;
  notes?: string;
  provider?: string;
  website_url?: string;
  payment_method?: string | null;
  auto_renew: boolean;
  auto_renewal_date?: string | null;
  trial_end_date?: string;
  cancellation_date?: string;
  pause_date?: string | null;
  resume_date?: string | null;
  rating?: number;
  next_payment_source?: string;
  start_date?: string;
  created_at: string;
  updated_at: string;
  reconciliation_snoozed_until?: string | null;
}

export const useSubscriptions = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ["subscriptions", user?.id];

  const {
    data: subscriptions = [],
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as DatabaseSubscription[];
    },
    enabled: !!user,
  });

  const createSubscription = useMutation({
    mutationKey: ["createSubscription"],
    mutationFn: async (
      subscription: Omit<
        DatabaseSubscription,
        "id" | "user_id" | "created_at" | "updated_at"
      >,
    ) => {
      if (!user) throw new Error("User not authenticated");

      const { data, error } = await supabase
        .from("subscriptions")
        .insert({
          ...subscription,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async (newItem) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<DatabaseSubscription[]>(queryKey);
      queryClient.setQueryData<DatabaseSubscription[]>(queryKey, (old = []) => [
        {
          ...newItem,
          id: crypto.randomUUID(),
          user_id: user?.id ?? "",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as DatabaseSubscription,
        ...old,
      ]);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const updateSubscription = useMutation({
    mutationKey: ["updateSubscription"],
    mutationFn: async ({
      id,
      ...updates
    }: Partial<DatabaseSubscription> & { id: string }) => {
      if (!user) throw new Error("User not authenticated");

      const { data, error } = await supabase
        .from("subscriptions")
        .update(updates)
        .eq("id", id)
        .eq("user_id", user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async ({ id, ...updates }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<DatabaseSubscription[]>(queryKey);
      queryClient.setQueryData<DatabaseSubscription[]>(queryKey, (old = []) =>
        old.map((sub) =>
          sub.id === id
            ? { ...sub, ...updates, updated_at: new Date().toISOString() }
            : sub,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteSubscription = useMutation({
    mutationKey: ["deleteSubscription"],
    mutationFn: async (id: string) => {
      if (!user) throw new Error("User not authenticated");

      const { error } = await supabase
        .from("subscriptions")
        .delete()
        .eq("id", id)
        .eq("user_id", user.id);

      if (error) throw error;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<DatabaseSubscription[]>(queryKey);
      queryClient.setQueryData<DatabaseSubscription[]>(queryKey, (old = []) =>
        old.filter((sub) => sub.id !== id),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    subscriptions,
    isLoading,
    error,
    createSubscription,
    updateSubscription,
    deleteSubscription,
  };
};
